<template>
  <div class="tables-view">
    <div v-if="loading" class="loading-container">
      <AppPageLoader>{{ $t('general.loading') }}</AppPageLoader>
    </div>

    <div v-else-if="error" class="error-container">
      <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
      <h2>{{ $t('forms.errorLoading') }}</h2>
      <p>{{ error }}</p>
      <router-link :to="`/locations/${locationId}`" class="back-button">
        <font-awesome-icon icon="fa-arrow-left" />
        {{ $t('tables.backToLocation') }}
      </router-link>
    </div>

    <div v-else-if="!location" class="error-container">
      <font-awesome-icon icon="fa-exclamation-triangle" class="error-icon" />
      <h2>{{ $t('forms.errorLoading') }}</h2>
      <p>{{ $t('locations.locationNotAvailable') }}</p>
      <router-link to="/establishments" class="back-button">
        <font-awesome-icon icon="fa-arrow-left" />
        {{ $t('establishments.backToEstablishments') }}
      </router-link>
    </div>

    <div v-else>
      <div class="header">
        <div class="header-left">
          <router-link :to="`/locations/${locationId}`" class="back-button">
            <font-awesome-icon icon="fa-arrow-left" />
            {{ $t('tables.backToLocation') }}
          </router-link>
          <div class="location-title">
            <h1>{{ $t('tables.manageTables') }} - {{ location.name }}</h1>
          </div>
        </div>
        <div v-if="canMutateInProject" class="header-actions">
          <BaseButton type="button" icon="fa-plus" @click="openCreateModal">
            {{ $t('tables.addTable') }}
          </BaseButton>
          <BaseButton type="button" variant="secondary" icon="fa-layer-group" @click="openBatchCreateModal">
            {{ $t('tables.batchAddTables') }}
          </BaseButton>
          <BaseButton type="button" variant="success" icon="fa-qrcode" @click="openGenerateQRModal">
            {{ $t('tables.generateQRCodes') }}
          </BaseButton>
        </div>
      </div>

      <TableList :location-id="locationId" :read-only="!canMutateInProject" ref="tableListRef" />

      <TableFormModal v-if="showTableModal" :table="selectedTable" :location-id="locationId"
        @close="showTableModal = false" @saved="handleTableSaved" />

      <TableBatchFormModal v-if="showBatchModal" :location-id="locationId" @close="showBatchModal = false"
        @saved="handleBatchSaved" />

      <QRCodeModal v-if="showQRModal" :table="selectedTable" :location-id="locationId" :single-mode="false"
        @close="showQRModal = false" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import { locationApi } from '@/services/api';
import { Location } from '@/types/establishments';
import { useI18n } from 'vue-i18n';
import TableList from '@/components/tables/TableList.vue';
import TableFormModal from '@/components/tables/TableFormModal.vue';
import TableBatchFormModal from '@/components/tables/TableBatchFormModal.vue';
import QRCodeModal from '@/components/tables/QRCodeModal.vue';
import { useProjectPermissions } from '@/composables/useProjectPermissions';
import BaseButton from '@/components/shared/BaseButton.vue';

const { canMutateInProject } = useProjectPermissions();

interface TableListRef {
  refresh: () => Promise<void>;
}

const route = useRoute();
const { t } = useI18n();

const locationId = computed(() => route.params.locationId as string);
const location = ref<Location | null>(null);
const loading = ref(true);
const error = ref<string | null>(null);
const tableListRef = ref<TableListRef | null>(null);

const showTableModal = ref(false);
const showBatchModal = ref(false);
const showQRModal = ref(false);
const selectedTable = ref(null);

const fetchLocation = async () => {
  loading.value = true;
  error.value = null;

  try {
    location.value = await locationApi.getById(locationId.value);
  } catch (err: any) {
    error.value = err.message || t('locations.errorFetchingLocation');
  } finally {
    loading.value = false;
  }
};

const openCreateModal = () => {
  selectedTable.value = null;
  showTableModal.value = true;
};

const openBatchCreateModal = () => {
  showBatchModal.value = true;
};

const openGenerateQRModal = () => {
  selectedTable.value = null;
  showQRModal.value = true;
};

const handleTableSaved = () => {
  showTableModal.value = false;

  if (tableListRef.value && 'refresh' in tableListRef.value) {
    tableListRef.value.refresh();
  }
};

const handleBatchSaved = () => {
  showBatchModal.value = false;

  if (tableListRef.value && 'refresh' in tableListRef.value) {
    tableListRef.value.refresh();
  }
};

onMounted(() => {
  fetchLocation();
});
</script>

<style scoped>
.tables-view {
  width: 100%;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #4b5563;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 0.5rem;
}

.back-button:hover {
  color: #D8670C;
}

.location-title h1 {
  margin: 0;
  font-size: 1.75rem;
}

:deep(.table-list-container:not(.no-container)) {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
}

.error-icon {
  color: #e74c3c;
  font-size: 2rem;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    margin-top: 1rem;
    width: 100%;
  }

  .header-actions :deep(.base-btn) {
    flex: 1;
  }
}
</style>